// AddRateCardItemModal.jsx
// "Add Item" modal for Step4SubElement — replaces the old prompt()-chain
// addItem() with a proper cascading form matching the RATE CARD screenshot
// (Class -> Asset Group -> Asset Element -> Main Element -> Sub Element,
// then Short Description / Currency / Unit / Rate). On ADD it persists the
// new row to Rate_Card via /insert_rate_card, then hands the saved item
// back to the caller (Step4SubElement) so it can be appended to the
// currently-open Main Element's sub-element table right away.
import React, { useEffect, useState } from "react";
import { api as axios } from "../config/apiConfig";
import { API_ORIGIN } from "../config/apiConfig";

const API_BASE = API_ORIGIN;
const NEW_SUB_ELEMENT_VALUE = "__new__";
const NEW_UNIT_VALUE = "__new_unit__";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  // Per explicit request, matches RateCard.jsx's own "Add Item" modal
  // (the theme's `modal-dialog-centered` Bootstrap markup) -- this modal
  // used to sit pinned near the top of the viewport (alignItems:
  // "flex-start") with a lighter overlay; now it's vertically centered,
  // same darkness (rgba(0,0,0,0.75)) as every other themed modal in the
  // app. `padding` stays so a tall form still scrolls within the
  // overlay instead of getting clipped off-screen on short viewports.
  background: "rgba(0,0,0,0.75)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 16px",
  overflowY: "auto",
  zIndex: 1000
};

const modalStyle = {
  background: "#fff",
  borderRadius: 6,
  padding: 20,
  width: "100%",
  maxWidth: 960,
  boxShadow: "0 8px 30px rgba(0,0,0,0.25)"
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 16
};

const titleStyle = {
  color: "#0f766e",
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: 0.4,
  textTransform: "uppercase"
};

const closeBtnStyle = {
  border: "none",
  background: "transparent",
  fontSize: 18,
  lineHeight: 1,
  color: "#666",
  cursor: "pointer"
};

// Per request, this modal now shows the same Data Date / OpCo Title /
// Project Name context fields RateCard.jsx's own Add Item modal shows --
// read-only, defaulted from whatever distinct RateYear/OpCo/ProjectName
// values already exist in Rate_Card (see the fetch effect below), not
// something the user picks here. 1fr/1fr/2fr mirrors RateCard.jsx's own
// col-md-3/col-md-3/col-md-6 split.
const gridRow0Style = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 2fr",
  gap: 12,
  marginBottom: 16
};

const gridRow1Style = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(140px, 1fr))",
  gap: 12
};

const gridRow2Style = {
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
  gap: 12,
  marginTop: 16
};

const labelStyle = { fontWeight: 700, fontSize: 12, marginBottom: 4, display: "block" };

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 20
};

export default function AddRateCardItemModal({
  show,
  onClose,
  onAdded,
  defaultCId,
  defaultAgId,
  defaultAgName,
  defaultAeId,
  defaultAeName,
  defaultMeId,
  defaultMeName
}) {
  const [classes, setClasses] = useState([]);
  const [assetGroups, setAssetGroups] = useState([]);
  const [assetElements, setAssetElements] = useState([]);
  const [mainElements, setMainElements] = useState([]);
  // Per request, Unit is now a dropdown instead of free text -- backed by
  // GET /get_units_rate (already built on the backend, see app.py's
  // units_rate_api blueprint), which returns the distinct Unit values
  // already used across Rate_Card (m2, m3, EA, LS, etc.) since there's no
  // separate Units master table. Fetched once per modal-open, same
  // unscoped pattern as Class/Main Element/Sub Element above.
  const [unitOptions, setUnitOptions] = useState([]);
  const [subElementOptions, setSubElementOptions] = useState([]);
  // Global master list from GET /sub-element (SE_ID + Sub_Element only, no
  // BOQ_Division, unscoped) -- merged with subElementOptions below so the
  // dropdown shows every known Sub Element, not just ones that already
  // have a Rate_Card row for this exact Asset Group/Element/Main Element.
  const [allSubElements, setAllSubElements] = useState([]);

  const [cId, setCId] = useState("");
  const [className, setClassName] = useState("");
  const [agId, setAgId] = useState("");
  const [agName, setAgName] = useState("");
  const [aeId, setAeId] = useState("");
  const [aeName, setAeName] = useState("");
  const [meId, setMeId] = useState("");
  const [meName, setMeName] = useState("");
  const [seId, setSeId] = useState("");
  const [seName, setSeName] = useState("");
  const [isNewSubElement, setIsNewSubElement] = useState(false);

  // Data Date (RateYear) / OpCo Title (OpCo) / Project Name (ProjectName) --
  // read-only context fields, same as RateCard.jsx's own Add Item modal.
  // Defaulted from the distinct values already sitting in Rate_Card (most
  // recent RateYear, first OpCo/ProjectName alphabetically) via the fetch
  // effect below, then sent through as-is on Add -- see handleAdd's payload.
  const [dataDate, setDataDate] = useState("");
  const [opCoTitle, setOpCoTitle] = useState("");
  const [projectName, setProjectName] = useState("");

  // BOQ_Division is a NOT NULL, foreign-keyed column on Rate_Card but isn't
  // a field this form shows the user -- it's captured from whichever
  // existing Rate_Card row the chosen Sub Element resolves to, and sent
  // through silently.
  const [boqDivision, setBoqDivision] = useState("");

  // Fallback BOQ Division for this Asset Group + Asset Element (any Main
  // Element), used when the current Main Element has no Rate_Card rows of
  // its own yet to pull one from -- see the effect below and its use in
  // handleSeChange. Per request, BOQ Division is a property of the Asset
  // Group/Element, not the Main Element, so this is a safe default rather
  // than falling back to "" (which trips FK_RateCard_BOQDivision).
  const [agAeBoqDivision, setAgAeBoqDivision] = useState("");

  const [shortDescription, setShortDescription] = useState("");
  const [unit, setUnit] = useState("");
  // Same "+ New X…" pattern already used for Sub Element above -- lets a
  // unit that isn't in unitOptions yet still be typed in free text instead
  // of blocking the form on the dropdown being incomplete.
  const [isNewUnit, setIsNewUnit] = useState(false);
  const [rate, setRate] = useState("");
  // Per explicit request ("Step 4 -- Add Item" flow): the Quantity the
  // user enters here must be saved into project_sub_elements right along
  // with the new Rate_Card row, not silently default to 1 -- see
  // handleAdd below and Step4SubElement's handleRateCardItemAdded, which
  // reads this off the returned item.
  const [quantity, setQuantity] = useState("1");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset + pre-fill from the row/main-element context Step4 opened this
  // modal from, every time it opens. Class/Asset Group/Asset Element/Main
  // Element start pre-selected to "where you already are" so Add Item is a
  // one-click flow in the common case; every dropdown can still be changed
  // if the new catalog item actually belongs somewhere else.
  useEffect(() => {
    if (!show) return;
    setError(null);
    setShortDescription("");
    setUnit("");
    setIsNewUnit(false);
    setRate("");
    setQuantity("1");
    setSeId("");
    setSeName("");
    setIsNewSubElement(false);
    setBoqDivision("");
    setDataDate("");
    setOpCoTitle("");
    setProjectName("");

    setCId(defaultCId || "");
    setAgId(defaultAgId || "");
    setAgName(defaultAgName || "");
    setAeId(defaultAeId || "");
    setAeName(defaultAeName || "");
    setMeId(defaultMeId || "");
    setMeName(defaultMeName || "");
  }, [show, defaultCId, defaultAgId, defaultAgName, defaultAeId, defaultAeName, defaultMeId, defaultMeName]);

  useEffect(() => {
    if (!show) return;
    axios.get(`${API_BASE}/class`)
      .then((res) => setClasses(res.data?.data || []))
      .catch(() => setClasses([]));
  }, [show]);

  useEffect(() => {
    if (!show) return;
    axios.get(`${API_BASE}/get_units_rate`)
      .then((res) => setUnitOptions(res.data?.data || []))
      .catch(() => setUnitOptions([]));
  }, [show]);

  // Same source RateCard.jsx itself loads (GET /rate-card, the full
  // Rate_Card table) -- reused here purely to derive the same three
  // distinct-value defaults it computes for its own Add Item modal: the
  // MOST RECENT RateYear (not the oldest -- sorted ascending, so the last
  // entry) and the first OpCo/ProjectName alphabetically. Fetched once per
  // modal-open, same unscoped pattern as Class/Unit above.
  useEffect(() => {
    if (!show) return;
    axios.get(`${API_BASE}/rate-card`)
      .then((res) => {
        const allRows = res.data?.data || [];
        const uniqueSorted = (key) =>
          Array.from(new Set(allRows.map((r) => r[key]).filter(Boolean))).sort();
        const years = uniqueSorted("RateYear");
        const opcos = uniqueSorted("OpCo");
        const projects = uniqueSorted("ProjectName");
        setDataDate(years.length ? String(years[years.length - 1]) : String(new Date().getFullYear()));
        setOpCoTitle(opcos[0] || "");
        setProjectName(projects[0] || "");
      })
      .catch(() => {
        setDataDate(String(new Date().getFullYear()));
        setOpCoTitle("");
        setProjectName("");
      });
  }, [show]);

  // FIX: className only used to get set inside handleClassChange, which
  // never fires when cId arrives pre-filled from Step4's context (only a
  // C_ID code, not a label) rather than from the user touching the
  // dropdown themselves. That left className stuck at "" whenever the
  // modal opened pre-selected -- the dropdown still SHOWED "Class 4"
  // (that's just the matching <option>'s label text), but the Class value
  // actually sent to /insert_rate_card stayed empty, which is why the
  // saved row came back with Class = NULL and the Step4 table showed "—"
  // instead of a Class badge. Deriving className here, reactively, from
  // whichever cId is currently set (pre-filled OR manually chosen) plus
  // whatever the classes list resolves it to (loaded async) keeps it
  // correct in both cases.
  useEffect(() => {
    if (!cId) return;
    const found = classes.find((c) => String(c.C_ID) === String(cId));
    if (found) setClassName(found.Class || "");
  }, [cId, classes]);

  useEffect(() => {
    if (!show || !cId) { setAssetGroups([]); return; }
    axios.get(`${API_BASE}/get_asset_groups_rate?c_id=${encodeURIComponent(cId)}`)
      .then((res) => setAssetGroups(res.data?.data || []))
      .catch(() => setAssetGroups([]));
  }, [show, cId]);

  useEffect(() => {
    if (!show || !cId || !agId) { setAssetElements([]); return; }
    axios.get(`${API_BASE}/get_asset_elements_rate?c_id=${encodeURIComponent(cId)}&ag_id=${encodeURIComponent(agId)}`)
      .then((res) => setAssetElements(res.data?.data || []))
      .catch(() => setAssetElements([]));
  }, [show, cId, agId]);

  // BOQ Division fallback -- exact match on Class + Asset Group + Asset
  // Element (per request), sourced from ANY existing Rate_Card row under
  // that exact combination regardless of Main Element -- see
  // agAeBoqDivision's declaration above for why.
  useEffect(() => {
    if (!show || !cId || !agId || !aeId) { setAgAeBoqDivision(""); return; }
    axios.get(`${API_BASE}/get_boq_division_for_asset?c_id=${encodeURIComponent(cId)}&ag_id=${encodeURIComponent(agId)}&ae_id=${encodeURIComponent(aeId)}`)
      .then((res) => setAgAeBoqDivision(res.data?.BOQ_Division || ""))
      .catch(() => setAgAeBoqDivision(""));
  }, [show, cId, agId, aeId]);

  // FIX: this used to call /get_main_elements_rate?c_id=&ag_id=&ae_id=,
  // which only returns Main Elements that already have an existing
  // Rate_Card row for this exact Asset Group/Asset Element combo. That
  // meant a brand-new Main Element -- e.g. one just added via Step 3's
  // "+ Add Main Element" (which inserts straight into the Main_Element
  // master table, POST /main-element) -- could never show up here, even
  // though this whole form exists to create the FIRST Rate_Card row for a
  // combination. Main_Element is a global master list, not scoped to any
  // particular Asset Group/Element (see GET /main-element's own query --
  // no ag_id/ae_id filter at all), so it's fetched once per modal-open
  // instead of being re-queried per Asset Group/Element selection.
  useEffect(() => {
    if (!show) return;
    axios.get(`${API_BASE}/main-element`)
      .then((res) => setMainElements(res.data?.data || []))
      .catch(() => setMainElements([]));
  }, [show]);

  useEffect(() => {
    if (!show || !cId || !agId || !aeId || !meId) { setSubElementOptions([]); return; }
    axios.get(`${API_BASE}/get_sub_elements_list_rate?c_id=${encodeURIComponent(cId)}&ag_id=${encodeURIComponent(agId)}&ae_id=${encodeURIComponent(aeId)}&me_id=${encodeURIComponent(meId)}`)
      .then((res) => setSubElementOptions(res.data?.data || []))
      .catch(() => setSubElementOptions([]));
  }, [show, cId, agId, aeId, meId]);

  // Same fix as Main Element above, applied to Sub Element: fetch the full
  // master list once per modal-open (GET /sub-element, unscoped) instead of
  // only ever offering Sub Elements that already have a Rate_Card row under
  // this exact Asset Group/Element/Main Element.
  useEffect(() => {
    if (!show) return;
    axios.get(`${API_BASE}/sub-element`)
      .then((res) => setAllSubElements(res.data?.data || []))
      .catch(() => setAllSubElements([]));
  }, [show]);

  // Dropdown list = every master Sub Element, but using the scoped row's
  // BOQ_Division (pulled from an existing Rate_Card entry) wherever this
  // Main Element already has one for that Sub Element -- keeps the "+ New
  // Sub Element..." option's behavior and the BOQ_Division fallback logic
  // below unchanged.
  const scopedBySeId = {};
  subElementOptions.forEach((s) => { scopedBySeId[String(s.SE_ID)] = s; });
  const subElementDisplayOptions = allSubElements.map((s) => scopedBySeId[String(s.SE_ID)] || s);
  subElementOptions.forEach((s) => {
    if (!subElementDisplayOptions.some((m) => String(m.SE_ID) === String(s.SE_ID))) {
      subElementDisplayOptions.push(s);
    }
  });

  if (!show) return null;

  const handleClassChange = (e) => {
    const val = e.target.value;
    const found = classes.find((c) => String(c.C_ID) === val);
    setCId(val);
    setClassName(found?.Class || "");
    setAgId(""); setAgName("");
    setAeId(""); setAeName("");
    setMeId(""); setMeName("");
    setSeId(""); setSeName(""); setIsNewSubElement(false); setBoqDivision("");
  };

  const handleAgChange = (e) => {
    const val = e.target.value;
    const found = assetGroups.find((a) => String(a.AG_ID) === val);
    setAgId(val);
    setAgName(found?.Asset_Group || "");
    setAeId(""); setAeName("");
    setMeId(""); setMeName("");
    setSeId(""); setSeName(""); setIsNewSubElement(false); setBoqDivision("");
  };

  const handleAeChange = (e) => {
    const val = e.target.value;
    const found = assetElements.find((a) => String(a.AE_ID) === val);
    setAeId(val);
    setAeName(found?.Asset_Element || "");
    setMeId(""); setMeName("");
    setSeId(""); setSeName(""); setIsNewSubElement(false); setBoqDivision("");
  };

  const handleMeChange = (e) => {
    const val = e.target.value;
    const found = mainElements.find((m) => String(m.ME_ID) === val);
    setMeId(val);
    setMeName(found?.Main_Element || "");
    setSeId(""); setSeName(""); setIsNewSubElement(false); setBoqDivision("");
  };

  const handleSeChange = (e) => {
    const val = e.target.value;
    if (val === NEW_SUB_ELEMENT_VALUE) {
      setIsNewSubElement(true);
      setSeId("");
      setSeName("");
      // No specific Rate_Card row to pull BOQ_Division from for a brand
      // new Sub Element -- prefer any existing row under this same Main
      // Element, then fall back to the Asset Group/Element-level default
      // (agAeBoqDivision) so a brand-new Main Element with zero rows of
      // its own still resolves to a valid BOQ_Division instead of "".
      setBoqDivision(subElementOptions[0]?.BOQ_Division || agAeBoqDivision || "");
      return;
    }
    setIsNewSubElement(false);
    const found = subElementDisplayOptions.find((s) => String(s.SE_ID) === val);
    setSeId(val);
    setSeName(found?.Sub_Element || "");
    // A Sub Element picked from the master list that this Main Element has
    // no existing Rate_Card row for yet won't have a BOQ_Division of its
    // own (see subElementDisplayOptions above) -- same fallback chain as
    // the "+ New Sub Element..." branch above in that case.
    setBoqDivision(found?.BOQ_Division || subElementOptions[0]?.BOQ_Division || agAeBoqDivision || "");
  };

  // Per request, Unit is now picked from a dropdown (unitOptions, from GET
  // /get_units_rate) instead of typed freely -- "+ New Unit..." keeps a
  // not-yet-listed unit reachable via a small text input, same pattern as
  // handleSeChange's "+ New Sub Element..." above.
  const handleUnitChange = (e) => {
    const val = e.target.value;
    if (val === NEW_UNIT_VALUE) {
      setIsNewUnit(true);
      setUnit("");
      return;
    }
    setIsNewUnit(false);
    setUnit(val);
  };

  const handleRateChange = (e) => {
    const v = e.target.value;
    if (v === "" || /^\d*\.?\d*$/.test(v)) {
      setRate(v);
    } else {
      alert("You can enter numbers only.");
    }
  };

  const handleQuantityChange = (e) => {
    const v = e.target.value;
    if (v === "" || /^\d*\.?\d*$/.test(v)) {
      setQuantity(v);
    } else {
      alert("You can enter numbers only.");
    }
  };

  const handleAdd = async () => {
    setError(null);

    if (!cId) { setError("Select a Class."); return; }
    if (!agId) { setError("Select an Asset Group."); return; }
    if (!aeId) { setError("Select an Asset Element."); return; }
    if (!meId) { setError("Select a Main Element."); return; }
    if (!seName) { setError("Select an existing Sub Element or choose \"+ New Sub Element\" and type one."); return; }
    if (rate === "" || Number.isNaN(Number(rate))) { setError("Enter a valid Rate."); return; }
    if (quantity === "" || Number.isNaN(Number(quantity)) || Number(quantity) <= 0) { setError("Enter a valid Quantity."); return; }
    // BOQ_Division is a required, foreign-keyed Rate_Card column this form
    // resolves silently (see handleSeChange) -- if every fallback came up
    // empty (this Asset Group/Element has no Rate_Card rows at all yet),
    // stop here with a clear message instead of letting /insert_rate_card
    // fail with a raw FK_RateCard_BOQDivision SQL error.
    if (!boqDivision) {
      setError("Could not determine a BOQ Division for this Asset Group / Asset Element. Add at least one Rate Card item for this combination first (via Rate Card management), then try again.");
      return;
    }

    setSaving(true);
    try {
      // A brand-new Sub Element (typed via "+ New Sub Element...") only
      // exists as text in this form so far -- insert it into the
      // Sub_Element master table first (POST /sub-element, same pattern as
      // Step 3's custom Main Element) so /insert_rate_card below gets a
      // real, DB-assigned SE_ID instead of leaving Rate_Card.SE_ID NULL.
      let finalSeId = seId;
      if (isNewSubElement) {
        const seRes = await axios.post(`${API_BASE}/sub-element`, { Sub_Element: seName });
        if (!seRes?.data?.success || !seRes.data?.SE_ID) {
          setError(seRes?.data?.error || "Failed to save new Sub Element.");
          setSaving(false);
          return;
        }
        finalSeId = seRes.data.SE_ID;
      }

      const payload = {
        C_ID: cId,
        Class: className,
        BOQ_Division: boqDivision,
        AG_ID: agId,
        Asset_Group: agName,
        AE_ID: aeId,
        Asset_Element: aeName,
        ME_ID: meId,
        Main_Element: meName,
        SE_ID: finalSeId,
        Sub_Element: seName,
        Short_Description: shortDescription || seName,
        Unit: unit,
        Rate: Number(rate),
        RateYear: dataDate,
        OpCo: opCoTitle,
        ProjectName: projectName
      };

      const res = await axios.post(`${API_BASE}/insert_rate_card`, payload);
      const data = res?.data;

      if (!data || data.success !== true) {
        setError(data?.error || "Failed to add item.");
        setSaving(false);
        return;
      }

      // Fix: project_sub_elements' ratecard_id MUST reference the
      // Rate_Card row just created above (its RC_ID) -- never the Sub
      // Element master row's own SE_ID, which is a completely different
      // id space (Sub_Element.SE_ID, not Rate_Card.RC_ID). Step4's
      // buildItemsPayload falls back to this item's SE_ID for
      // ratecard_id only when RC_ID is missing -- that fallback exists
      // for catalog rows loaded from /get_sub_elements_rate (whose
      // "SE_ID" field is actually Rate_Card's RC_ID under a mislabeled
      // name), not for a row created here. If insert_rate_card didn't
      // hand back a real RC_ID (e.g. SCOPE_IDENTITY() came back empty),
      // silently falling through to finalSeId would save a Sub Element
      // id as the ratecard_id -- exactly the bad data found in
      // project_sub_elements (ratecard_id values like "SE-0" instead of
      // a real Rate_Card id). Stop here instead of writing a corrupt
      // reference.
      if (!data.RC_ID) {
        setError("The Rate Card item was created, but the server didn't return its new Rate Card ID, so it can't be safely linked to this project yet. Please refresh Rate Card and try adding it again.");
        setSaving(false);
        return;
      }

      const qty = Number(quantity) || 1;
      const newSE = {
        // Per explicit request, Step4's project_sub_elements insert must
        // reference the just-created Rate_Card record -- RC_ID is what
        // /insert_rate_card returns for the row it just inserted (see
        // `data.RC_ID` above), so this is the real, DB-assigned id, not a
        // locally-generated placeholder. SE_ID here is only ever used as
        // a React list key, never written back as ratecard_id.
        SE_ID: data.RC_ID,
        RC_ID: data.RC_ID,
        Sub_Element: seName,
        Short_Description: shortDescription || seName,
        Unit: unit,
        Rate: Number(rate),
        Quantity: qty,
        Cost: Number(rate) * qty,
        asset_class: className || null
      };

      setSaving(false);
      onAdded && onAdded(newSE);
    } catch (err) {
      console.error("insert_rate_card failed:", err);
      setError(err?.response?.data?.error || "Failed to add item.");
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={titleStyle}>Add Item Details</div>
          <button type="button" style={closeBtnStyle} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {error && <div className="alert alert-danger py-2 px-3 mb-3">{error}</div>}

        <div style={gridRow0Style}>
          <div>
            <label style={labelStyle}>Data Date</label>
            <input type="text" className="form-control form-control-sm bg-light" value={dataDate || "—"} readOnly />
          </div>

          <div>
            <label style={labelStyle}>OpCo Title</label>
            <input type="text" className="form-control form-control-sm bg-light" value={opCoTitle || "—"} readOnly />
          </div>

          <div>
            <label style={labelStyle}>Project Name</label>
            <input type="text" className="form-control form-control-sm bg-light" value={projectName || "—"} readOnly />
          </div>
        </div>

        <div style={gridRow1Style}>
          <div>
            <label style={labelStyle}>Class</label>
            <select className="form-select form-select-sm" value={cId} onChange={handleClassChange}>
              <option value="">Select</option>
              {classes.map((c) => (
                <option key={c.C_ID} value={c.C_ID}>{c.Class}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Asset Group</label>
            <select className="form-select form-select-sm" value={agId} onChange={handleAgChange} disabled={!cId}>
              <option value="">Select</option>
              {assetGroups.map((a) => (
                <option key={a.AG_ID} value={a.AG_ID}>{a.Asset_Group}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Asset Element</label>
            <select className="form-select form-select-sm" value={aeId} onChange={handleAeChange} disabled={!agId}>
              <option value="">Select</option>
              {assetElements.map((a) => (
                <option key={a.AE_ID} value={a.AE_ID}>{a.Asset_Element}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Main Element</label>
            <select className="form-select form-select-sm" value={meId} onChange={handleMeChange} disabled={!aeId}>
              <option value="">Select</option>
              {mainElements.map((m) => (
                <option key={m.ME_ID} value={m.ME_ID}>{m.Main_Element}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Sub Element</label>
            <select
              className="form-select form-select-sm"
              value={isNewSubElement ? NEW_SUB_ELEMENT_VALUE : seId}
              onChange={handleSeChange}
              disabled={!meId}
            >
              <option value="">Select</option>
              {subElementDisplayOptions.map((s) => (
                <option key={s.SE_ID} value={s.SE_ID}>{s.Sub_Element}</option>
              ))}
              <option value={NEW_SUB_ELEMENT_VALUE}>+ New Sub Element…</option>
            </select>
            {isNewSubElement && (
              <input
                type="text"
                className="form-control form-control-sm mt-1"
                placeholder="Enter new Sub Element name"
                value={seName}
                onChange={(e) => setSeName(e.target.value)}
              />
            )}
          </div>
        </div>

        <div style={gridRow2Style}>
          <div>
            <label style={labelStyle}>Short Description</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              placeholder={seName || "Short description"}
            />
          </div>

          <div>
            <label style={labelStyle}>Currency</label>
            <input type="text" className="form-control form-control-sm" value="SAR" readOnly />
          </div>

          <div>
            <label style={labelStyle}>Unit</label>
            <select
              className="form-select form-select-sm"
              value={isNewUnit ? NEW_UNIT_VALUE : unit}
              onChange={handleUnitChange}
            >
              <option value="">Select</option>
              {unitOptions.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
              <option value={NEW_UNIT_VALUE}>+ New Unit…</option>
            </select>
            {isNewUnit && (
              <input
                type="text"
                className="form-control form-control-sm mt-1"
                placeholder="e.g. m2"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              />
            )}
          </div>

          <div>
            <label style={labelStyle}>Rate (SAR)</label>
            <input
              type="text"
              inputMode="decimal"
              className="form-control form-control-sm"
              value={rate}
              onChange={handleRateChange}
            />
          </div>

          <div>
            <label style={labelStyle}>Quantity</label>
            <input
              type="text"
              inputMode="decimal"
              className="form-control form-control-sm"
              value={quantity}
              onChange={handleQuantityChange}
            />
          </div>
        </div>

        <div style={footerStyle}>
          <button type="button" className="btn-teal" onClick={handleAdd} disabled={saving}>
            {saving ? "Adding..." : "ADD"}
          </button>
        </div>
      </div>
    </div>
  );
}